import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, Subscription, of, fromEvent } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { Users } from './Admin/reporting/Users';
import { UtilityService } from './Services/utility.service';
import data_settingMAO from '../assets/json/MaoProject.json';
import { Router, NavigationExtras, NavigationEnd } from '@angular/router';
import { NgZone } from '@angular/core';
import SwalLocal from 'sweetalert2';


const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json', 'withCredentials': 'true', 'Authorization': 'true' })
};

const hostGeneral = data_settingMAO.RestApi;

declare var $: any;

/* **** 2025-10-07 JBDCR:  Add Interface for typing the session in localStorage */
export interface SessionMAO {
  validate: string;
  name?: string;
  UserID?: number;
  CompanyID?: number | null;
  PublisherID?: number | null;
  RoleID?: number | null;
  VerticalID?: number | null;
  Token?: string;
  ExpiresOn?: string;
  Email?: string;
  RoleName?: string;
}

@Injectable({
  providedIn: 'root'
})
export class UsersAuthService {

  /* **** 2025-10-07 JBDCR:  Constants and states for expiration watchdog */
  private readonly STORAGE_KEY = 'SessionMAO';
  private readonly EXPIRED_FLAG_KEY = 'MAO__SessionExpiredFlag';
  private readonly RETURN_URL_KEY = 'MAO__ReturnUrl';
  private readonly EXPIRED_FLAG_TS_KEY = 'MAO__SessionExpiredFlag_TS';
  private readonly EXPIRED_FLAG_NAV_KEY = 'MAO__SessionExpiredNav';
  private readonly EXPIRED_USER_EMAIL_KEY = 'MAO__ExpiredUserEmail';


  private storageSub: Subscription | null = null;
  private isHandlingExpiry = false;
  private routeEventsSub: Subscription | null = null;
  private routerInitDone = false;


  constructor(
    private http: HttpClient,
    private _utilityService: UtilityService,
    private router: Router,
    private zone: NgZone
  ) { }

  LogInBackup(Email: string, Password: string) {
    var url = hostGeneral + "api/UserAuth/LogIN";
    let paramsx = new HttpParams();
    paramsx = paramsx.append("pEmail", Email);
    paramsx = paramsx.append("pPassword", Password);
    return this.http.get(url, { params: paramsx }).toPromise();
  }

  LogIn(Email: string, Password: string, NeedsHash: boolean): Observable<Users> {
    let _userSend = new Users();
    _userSend.Email = Email;
    _userSend.Password = Password;
    _userSend.NeedsHash = NeedsHash;
    return this.http.post<Users>(`${hostGeneral}/api/UserAuth/LogIN`, _userSend, httpOptions);
  }

  Register(register: any): Observable<any> {
    return this.http.post<any>(`${hostGeneral}/api/UserAuth/Register`, register, httpOptions);
  }

  VerifyCredentials(): Observable<boolean> {

    const urlToRequest = `${hostGeneral}/api/UserAuth/GetUserByID`;
    let myItemSession = localStorage.getItem("SessionMAO");
    let obj = $.parseJSON(myItemSession);

    if (!obj || obj.Validate === 'false') {
      return of(false);
    }

    let paramsx = new HttpParams().set("UserID", obj.UserID);

    return this.http.get<any>(urlToRequest, {
      headers: httpOptions.headers,
      params: paramsx
    }).pipe(
      map((resp: any) => {
        if (resp.code !== 0) {
          return false;
        }

        if (resp.Value.RoleID !== obj.RoleID) {

          this._utilityService.SaveAdminLogFieldChange(
            "Sessions",
            "Roles",
            obj.UserID,
            "UsersAuthService",
            obj.UserID,
            1,
            "Role ID",
            resp.Value.RoleID,
            obj.RoleID,
            `User ${obj.UserID} attempted to modify their session ID by impersonating another roleID.`,
            9
          );

          return false;
        }

        return true;
      })
    );


  }

  // **** 2024-09-11 JFRH: saveAdminLogUnauthent() is the unauthenticated user version for ApiService.saveAdminLog()
  saveAdminLogUnauthent(post: any): Observable<any> {
    return this.http.post<any>(`${hostGeneral}/api/UserAuth/SaveLog`, post, httpOptions);
  }

  /** Reads and parses the session from localStorage. */
  getSession(): SessionMAO | null {
    try {
      var raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) return null;
      return JSON.parse(raw);
    } catch (e) {
      return null;
    }
  }

  /** Is there a valid session? (validate === "true" and Token present). Expiration is enforced by the backend via 401. */
  isAuthenticated(): boolean {
    var s = this.getSession();
    if (!s) return false;
    if (s.validate !== 'true') return false;
    return !!s.Token;
  }

  /** Clears the local session. */
  clearSession(): void {
    try {
      localStorage.removeItem(this.STORAGE_KEY);
    } catch (e) {
    }
  }

  /* **** 2025-10-07 JBDCR:  Saves the current route to return after login */
  private rememberReturnUrl(url: string): void {
    try {
      if (url && !this.isAuthFlowRoute(url)) {
        localStorage.setItem(this.RETURN_URL_KEY, url);
      }
    } catch { }
  }


  /* **** 2025-10-07 JBDCR:  Obtains the saved route and clears it */
  consumeReturnUrlOrDefault(): string | null {
    try {
      const u = localStorage.getItem(this.RETURN_URL_KEY);
      if (u) {
        localStorage.removeItem(this.RETURN_URL_KEY);
        return u;
      }
    } catch (e) { /* noop */ }
    return null;
  }

  /**
   * Unique expiration flow:
   * - Marks expiration flag (for other tabs and for Login).
   * - Clears local session.
   * - Redirects FIRST to /pages/login (the Swal will be shown in the login).
   * - Avoid setting flag if already on /pages/login
   */
  /* **** 2025-10-08 JBDCR: Don't set flag and redirect if already on login/recovery */
  redirectToLoginWithExpiredFlag(): void {
    if (this.isHandlingExpiry) return;

    const currentPath = this.getCurrentPath();
    if (this.isAuthFlowRoute(currentPath)) {
      this.clearSession();
      return; // We are already on login/recovery: don't set flags or navigate
    }

    this.isHandlingExpiry = true;

    // Remember return URL (only if not auth route; your rememberReturnUrl already filters it)
    if (currentPath && this.rememberReturnUrl) {
      this.rememberReturnUrl(currentPath);
    }

    // **** 2025-10-08 JBDCR: Save email of the user whose session expired (for comparison on re-login)
    const s = this.getSession && this.getSession();
    const expiredEmail = s && s.Email ? String(s.Email).toLowerCase().trim() : null;
    if (expiredEmail) {
      try { localStorage.setItem(this.EXPIRED_USER_EMAIL_KEY, expiredEmail); } catch { /* noop */ }
    }

    // Flags of expiration:
    // - localStorage (persistent) with timestamp
    // - sessionStorage (per tab) to ensure Swal in the login of this navigation
    try {
      localStorage.setItem(this.EXPIRED_FLAG_KEY, '1');
      localStorage.setItem(this.EXPIRED_FLAG_TS_KEY, String(Date.now()));
    } catch { /* noop */ }
    try {
      sessionStorage.setItem(this.EXPIRED_FLAG_NAV_KEY, '1');
    } catch { /* noop */ }

    // Clear local session before going to login
    this.clearSession();

    const extras: NavigationExtras = { state: { sessionExpired: true } };

    this.zone.run(() => {
      this.router.navigate(['/pages/login'], extras).then(
        () => { setTimeout(() => { this.isHandlingExpiry = false; }, 500); },
        () => { setTimeout(() => { this.isHandlingExpiry = false; }, 500); }
      );
    });
  }


  // **** 2025-10-08 JBDCR: Takes the email of the expired user (removes it after reading)
  consumeExpiredUserEmail(): string | null {
    try {
      const v = localStorage.getItem(this.EXPIRED_USER_EMAIL_KEY);
      if (v) {
        localStorage.removeItem(this.EXPIRED_USER_EMAIL_KEY);
        return v;
      }
    } catch { }
    return null;
  }

  // **** 2025-10-08 JBDCR: Clear returnUrl if the re-entering user does not match
  clearReturnUrl(): void {
    try { localStorage.removeItem(this.RETURN_URL_KEY); } catch { }
  }

  /**
   * Shows Swal ONLY on login if coming from expiration.
   * Use it in the LoginComponent (ngOnInit), passing history.state.
   */
  async showExpiredSwalIfFlagPresent(navigationState: any): Promise<void> {
    const cameWithState = navigationState && navigationState.sessionExpired === true;

    // Checks by-tab
    let navFlag = false;
    try {
      navFlag = sessionStorage.getItem(this.EXPIRED_FLAG_NAV_KEY) === '1';
    } catch { }

    // Compatibility: flags in localStorage (with timestamp)
    let localFlag = false, ts = 0;
    try {
      localFlag = localStorage.getItem(this.EXPIRED_FLAG_KEY) === '1';
      ts = parseInt(localStorage.getItem(this.EXPIRED_FLAG_TS_KEY) || '0', 10);
    } catch { }
    const now = Date.now();
    const fresh = ts > 0 && (now - ts) <= 10 * 60 * 1000;

    const shouldShow = cameWithState || navFlag || (localFlag && fresh);

    if (shouldShow) {
      // Cleans up to avoid re-showing
      try { sessionStorage.removeItem(this.EXPIRED_FLAG_NAV_KEY); } catch { }
      try {
        localStorage.removeItem(this.EXPIRED_FLAG_KEY);
        localStorage.removeItem(this.EXPIRED_FLAG_TS_KEY);
      } catch { }

      try {
        await SwalLocal({
          title: 'Session expired',
          text: 'Your session has expired. Please log in again.',
          type: 'warning',
          showCancelButton: false,
          confirmButtonText: 'OK',
          allowOutsideClick: false,
          allowEscapeKey: false,
          allowEnterKey: true,
          buttonsStyling: true,
          confirmButtonColor: '#01b88e'
        }).catch(SwalLocal.noop);
      } catch { }
    } else {
      // Old garbage: clean up silently
      if (navFlag) { try { sessionStorage.removeItem(this.EXPIRED_FLAG_NAV_KEY); } catch { } }
      if (localFlag || ts) {
        try {
          localStorage.removeItem(this.EXPIRED_FLAG_KEY);
          localStorage.removeItem(this.EXPIRED_FLAG_TS_KEY);
        } catch { }
      }
    }
  }

  /**
   * Inits cross-tab session sync and router tracking.
   * We will call this method when starting the app (APP_INITIALIZER in AppModule).
   */
  /* **** 2025-10-08 JBDCR: Pause watchdog on auth routes (login/recovery) and resume outside */
  initSessionListeners(): void {
    // Cross-tab sync: if another tab sets the expired flag, redirect this tab too
    if (!this.storageSub) {
      this.storageSub = fromEvent(window as any, 'storage').subscribe((e: any) => {
        if (e && e.key === this.EXPIRED_FLAG_KEY && e.newValue === '1') {
          const u = this.getCurrentPath();
          if (this.isAuthFlowRoute(u)) return;
          this.redirectToLoginWithExpiredFlag();
        }
      });
    }

    if (!this.routeEventsSub) {
      this.routeEventsSub = this.router.events
        .pipe(filter((ev: any) => ev instanceof NavigationEnd))
        .subscribe((ev: NavigationEnd) => {
          if (!this.routerInitDone) {
            this.routerInitDone = true;
          }
        });
    }
  }

  private isAuthFlowRoute(url: string): boolean {
    if (!url) return false;
    const path = url.split('?')[0].split('#')[0];
    // We treat root '/' as auth flow because it usually redirects to login.
    return path === '/' ||
      path === '' || 
      path.startsWith('/pages/login') || 
      path.startsWith('/pages/recovery') || 
      path.startsWith('/pages/forgotpassword') ||
      path.startsWith('/pages/register');
  }

  private getCurrentPath(): string {
    try {
      if (this.router && this.router.url) return this.router.url;
    } catch { }
    try {
      if (window && window.location && typeof window.location.pathname === 'string') {
        return window.location.pathname;
      }
    } catch { }
    return '';
  }

}
